'use client'

import { useRef, useState } from 'react'
import { useMutation } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import type { ExpenseCategory, Settings } from '@garage/shared'
import { useSnackbar } from '@/ui'
import { api, ApiError } from '@/lib/api.js'
import { useSettings } from '@/lib/hooks.js'

const FILENAME_FIELD = {
  general: 'excelSyncGeneralFilename',
  parts: 'excelSyncPartsFilename',
  paint: 'excelSyncPaintFilename',
} as const satisfies Record<ExpenseCategory, string>

interface ExcelImportFieldProps {
  category: ExpenseCategory
  label: string
}

/**
 * A file picker for one Accounting-sync category, uploading straight to the
 * server's excel-imports folder — the workshop never has to go find that
 * folder on disk or type a filename by hand. Uploads immediately on
 * selection, independent of the page's own Save button, since there is
 * nothing else about this field to stage or discard.
 *
 * Deliberately does NOT invalidate the shared settings query on upload: the
 * Settings page resets its whole form from that query whenever it changes,
 * so doing so here would wipe out any OTHER field the user was mid-editing
 * elsewhere on the page. The upload response updates this component's own
 * local display instead.
 */
export function ExcelImportField({ category, label }: ExcelImportFieldProps) {
  const snackbar = useSnackbar()
  const { t } = useTranslation()
  const { data: settings } = useSettings()
  const inputRef = useRef<HTMLInputElement>(null)

  // null until this component itself uploads something — until then, the
  // displayed filename is just whatever's already saved.
  const [justUploaded, setJustUploaded] = useState<string | null>(null)
  const filename = justUploaded ?? (settings?.[FILENAME_FIELD[category]] || '')

  const uploadMutation = useMutation({
    mutationFn: (file: File) => api.settings.uploadExcelImport(category, file),
    onSuccess: (data: Settings) => {
      setJustUploaded(data[FILENAME_FIELD[category]] || '')
      snackbar.success(t('excelImport.fileUploaded', { label }))
    },
    onError: (err) => {
      snackbar.error(err instanceof ApiError ? err.message : t('excelImport.uploadFailed'))
    },
  })

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.currentTarget.files?.[0]
    if (!file) return
    uploadMutation.mutate(file)
    // Lets picking the same filename again re-trigger onChange (e.g. after
    // fixing the sheet and re-uploading under the same name).
    e.currentTarget.value = ''
  }

  return (
    <div className="space-y-2">
      <span className="block text-label-md text-on-surface">{label}</span>
      <div className="flex flex-wrap items-center gap-3">
        {/*
          `display: none` input opened via ref.click(), not an `sr-only` input
          behind a <label>. An sr-only input is absolutely positioned with no
          positioned ancestor, so on click the browser focused it and
          scrolled it into view — but this app's html/body are overflow:hidden
          and the real scroller is <main>, so it scrolled html/body instead,
          sliding the whole app off-screen to plain black until a refresh.
          A display:none input can't take focus, so nothing scrolls.
        */}
        <input
          ref={inputRef}
          type="file"
          accept=".xlsx,.xls,.csv"
          onChange={handleChange}
          className="hidden"
          disabled={uploadMutation.isPending}
        />
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          disabled={uploadMutation.isPending}
          className="inline-flex items-center justify-center px-4 py-2 rounded-full border border-outline bg-transparent text-on-surface text-label-lg interactive hover:bg-surface-high active:bg-surface-high cursor-pointer w-fit"
        >
          {uploadMutation.isPending ? t('excelImport.uploading') : t('excelImport.chooseFile')}
        </button>
        <span className="text-body-sm text-on-surface-variant">
          {filename || t('excelImport.noFileUploadedYet')}
        </span>
      </div>
    </div>
  )
}
